import { useCallback, useEffect, useState } from 'react';
import { useContract } from './useContract';
import LuckyDrawABI from 'contracts/luckyDraw.json';
import { LUCKY_DRAW_CONTRACT_ADDRESS } from 'constant';
import BigNumber from 'bignumber.js';

export interface LuckyDrawContractData {
    totalWeight: number;
    isActive: boolean;
    currentRound: number;
    rewardCount: number;
    loading: boolean;
    error: string | null;
}

export function useLuckyDrawContract(): LuckyDrawContractData {
    const contract = useContract(LUCKY_DRAW_CONTRACT_ADDRESS, LuckyDrawABI, false);

    const [totalWeight, setTotalWeight] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [currentRound, setCurrentRound] = useState(0);
    const [rewardCount, setRewardCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchContractData = useCallback(async () => {
        if (!contract) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // 获取合约中实际存在的数据
            const [paused, currentRoundData, rewardCountData] = await Promise.all([
                contract.paused(),
                contract.getCurrentRound(),
                contract.rewardCount(),
            ]);

            // 获取合约基本状态信息
            const totalWeightData = await contract.WEIGHT_BASE();

            setTotalWeight(Number(totalWeightData));
            setIsActive(!paused); // 如果合约没有暂停，则认为是活跃的
            setCurrentRound(Number(currentRoundData));
            setRewardCount(Number(rewardCountData));
        } catch (err) {
            console.error('Failed to fetch lucky draw contract data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch contract data');
            // 设置默认值作为回退
            setTotalWeight(1000);
            setIsActive(true);
            setCurrentRound(1);
            setRewardCount(7);
        } finally {
            setLoading(false);
        }
    }, [contract]);

    useEffect(() => {
        fetchContractData();
    }, [fetchContractData]);

    return {
        totalWeight,
        isActive,
        currentRound,
        rewardCount,
        loading,
        error,
    };
}

// 格式化权重显示
export function formatProbability(weight: string, totalWeight: number): string {
    const w = parseFloat(weight);
    const percentage = (w / totalWeight) * 100;

    let formatted: string;
    if (percentage >= 1) {
        formatted = percentage.toFixed(1);
    } else if (percentage >= 0.1) {
        formatted = percentage.toFixed(2);
    } else {
        formatted = percentage.toFixed(3);
    }

    // 移除末尾的零和小数点（如果整个小数部分都是零）
    formatted = formatted.replace(/\.?0+$/, '');
    return `${formatted}%`;
}

// 格式化奖品数量显示
export function formatPrizeAmount(amount: string, name: string, decimal?: number): string {
    // 使用 BigNumber 确保精度
    const amountBN = new BigNumber(amount);

    // 如果有 decimal 字段，使用它来计算正确的显示值
    if (decimal !== undefined && decimal !== null) {
        const divisor = new BigNumber(10).pow(decimal);
        const displayAmount = amountBN.dividedBy(divisor);

        // 使用 toFixed() 并指定足够的小数位以确保精度，然后移除末尾的零
        // 使用 decimal 作为最大小数位数，但至少保留 18 位以确保精度
        const maxDecimals = Math.max(decimal, 18);
        let formatted = displayAmount.toFixed(maxDecimals);

        // 移除末尾的零和小数点（如果整个小数部分都是零）
        formatted = formatted.replace(/\.?0+$/, '');

        return `${formatted} ${name}`;
    }

    // 回退：直接显示原始数值
    let formatted = amountBN.toFixed(18);
    formatted = formatted.replace(/\.?0+$/, '');
    return `${formatted} ${name}`;
}
